import {
  MAX_ACTIVITY_AGE_MS,
  MAX_ACTIVITY_MINUTES,
  idleMsFor,
  minuteStart,
  type ActivityContext,
  type ActivityMinute,
} from './time-tracking';

/**
 * Time tracking (T-77), the tab's half, shared by the web app (frontend/src/hooks/use-activity-tracking.ts)
 * (the Chrome extension counts in its background instead, extension/lib/background-tracker.ts): which minutes this tab counts, held in memory until they're sent. A
 * minute counts when, during it, the tab is visible and focused and the person typed, clicked, scrolled
 * or touched recently (2 minutes, or 5 on receiving pages: `idleMsFor`). The minute is kept against the
 * tab's area and open record at that moment (the latest sample in the minute wins). The Worker keeps one
 * row per person per minute, so other tabs and devices counting the same minute never add to it.
 *
 * Nothing is stored in the browser: a tab closed before it could send loses at most its last minute.
 * Ported from the software factory's people's time (its T-102).
 */

/**
 * Keepalive requests share 64 KiB in flight across the page. Unload batches are cut by their JSON size
 * and kept under this, less what earlier keepalive requests still have on their way; the rest leaves room
 * for the envelope and headers.
 */
export const KEEPALIVE_BUDGET_BYTES = 48_000;
/** The request around the minutes (`{"minutes":[…]}`, the extension's fields). */
const BATCH_ENVELOPE_BYTES = 200;

/** A minute's share of a request body, in bytes: UTF-8 takes 1 to 3 bytes a UTF-16 unit, so 3 for any non-ASCII. */
function minuteBytes(m: ActivityMinute): number {
  const json = JSON.stringify(m);
  let bytes = json.length + 1;
  for (let i = 0; i < json.length; i++) if (json.charCodeAt(i) > 0x7f) bytes += 2;
  return bytes;
}

export interface ActivityPage {
  now(): number;
  visible(): boolean;
  focused(): boolean;
  /** Where the tab is; null while it shouldn't count (signed out, no team). */
  context(): ActivityContext | null;
}

/** → whether the Worker took them (a refusal it will never take, like a bad body, counts as taken). */
export type ActivitySender = (minutes: ActivityMinute[], opts: { keepalive: boolean }) => Promise<boolean>;

/** Whether the tab counts right now, in a page of this area. */
export function isActive(
  page: Pick<ActivityPage, 'visible' | 'focused'>,
  lastInputAt: number,
  now: number,
  idleMs: number
): boolean {
  return page.visible() && page.focused() && now - lastInputAt <= idleMs;
}

/**
 * Whether a failed send is worth trying again: a lost connection, a server error, a session being
 * renewed, a timeout or a rate limit. Any other 4xx is a refusal that will never be taken.
 */
export function activityTaken(status: number): boolean {
  if (status >= 200 && status < 300) return true;
  return status >= 400 && status < 500 && status !== 401 && status !== 408 && status !== 429;
}

export class ActivityTracker {
  private lastInputAt = Number.NEGATIVE_INFINITY;
  private readonly pending = new Map<number, ActivityMinute>();
  private sending = false;
  /** Bytes of keepalive requests not yet answered. */
  private keepaliveInFlight = 0;

  constructor(
    private readonly page: ActivityPage,
    private readonly send: ActivitySender
  ) {}

  /** The person typed, clicked, scrolled or touched. */
  input(): void {
    this.lastInputAt = this.page.now();
    this.sample();
  }

  /** Counts the current minute if the tab is active now. */
  sample(): void {
    const now = this.page.now();
    const context = this.page.context();
    if (!context) return;
    if (!isActive(this.page, this.lastInputAt, now, idleMsFor(context.area))) return;
    const minute = minuteStart(now);
    // Re-inserted so the map stays oldest first.
    this.pending.delete(minute);
    this.pending.set(minute, { minute: new Date(minute).toISOString(), ...context });
  }

  /** The minutes waiting to be sent, oldest first (tests and the hook). */
  waiting(): ActivityMinute[] {
    return [...this.pending.values()];
  }

  /**
   * Sends the oldest waiting minutes (at most MAX_ACTIVITY_MINUTES), and the next batch while each is
   * taken. Kept for the next try if a send fails; a minute counted again while it was on its way stays to
   * be sent again. `keepalive` when the tab is being hidden or closed: it goes even if one is already on
   * its way, and its batches leave at once, since a closing page can't wait for answers. They're cut by
   * size and stop at the browser's keepalive allowance (KEEPALIVE_BUDGET_BYTES, less requests still in
   * flight), so none is refused; the newer minutes past it wait for the next send.
   */
  async flush(keepalive = false): Promise<void> {
    this.dropStale();
    if (!this.pending.size || (this.sending && !keepalive)) return;
    if (keepalive) {
      const batches = this.keepaliveBatches(KEEPALIVE_BUDGET_BYTES - this.keepaliveInFlight);
      await Promise.all(
        batches.map(async ({ batch, bytes }) => {
          this.keepaliveInFlight += bytes;
          try {
            await this.sendBatch(batch, true);
          } finally {
            this.keepaliveInFlight -= bytes;
          }
        })
      );
      return;
    }
    this.sending = true;
    try {
      while (this.pending.size) {
        const batch = [...this.pending.entries()].slice(0, MAX_ACTIVITY_MINUTES);
        if (!(await this.sendBatch(batch, false)) || batch.length < MAX_ACTIVITY_MINUTES) return;
      }
    } finally {
      this.sending = false;
    }
  }

  /** The oldest waiting minutes cut into requests that together fit in `budget` bytes. */
  private keepaliveBatches(budget: number): Array<{ batch: Array<[number, ActivityMinute]>; bytes: number }> {
    const batches: Array<{ batch: Array<[number, ActivityMinute]>; bytes: number }> = [];
    let left = budget;
    let current: { batch: Array<[number, ActivityMinute]>; bytes: number } | null = null;
    for (const entry of this.pending.entries()) {
      const size = minuteBytes(entry[1]);
      if (!current || current.batch.length >= MAX_ACTIVITY_MINUTES) {
        if (left < BATCH_ENVELOPE_BYTES + size) break;
        current = { batch: [], bytes: BATCH_ENVELOPE_BYTES };
        left -= BATCH_ENVELOPE_BYTES;
        batches.push(current);
      }
      if (left < size) break;
      current.batch.push(entry);
      current.bytes += size;
      left -= size;
    }
    return batches.filter((b) => b.batch.length);
  }

  /** → whether it was taken; what was taken (and not counted again meanwhile) stops waiting. */
  private async sendBatch(batch: Array<[number, ActivityMinute]>, keepalive: boolean): Promise<boolean> {
    let ok = false;
    try {
      ok = await this.send(
        batch.map(([, m]) => m),
        { keepalive }
      );
    } catch {
      ok = false;
    }
    if (!ok) return false;
    for (const [minute, sent] of batch) {
      if (this.pending.get(minute) === sent) this.pending.delete(minute);
    }
    return true;
  }

  /** Minutes the Worker would refuse as too old. */
  private dropStale(): void {
    const oldest = minuteStart(this.page.now()) - MAX_ACTIVITY_AGE_MS + 60_000;
    for (const minute of this.pending.keys()) {
      if (minute < oldest) this.pending.delete(minute);
      else break;
    }
  }
}
